import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { FiCheck, FiShield } from "react-icons/fi";
import { AppButton, MessageBanner } from "@/components/ui/primitives";
import { PageSkeleton } from "@/components/ui/PageSkeleton";
import { AuthLayout } from "@/domains/auth/layouts/AuthLayout";
import { useAuthStore } from "@/stores/useAuthStore";
import { clearAuthReturnDestination, saveAuthReturnDestination } from "@/utils/authReturnDestination";
import { useTranslation } from "react-i18next";
import {
  getMcpAuthorizationRequest,
  submitMcpAuthorizationDecision,
  type McpAuthorizationRequest,
} from "../hooks/useMcpOAuth";

const getRequestId = () => {
  const values = new URLSearchParams(window.location.search).getAll("request_id");
  return values.length === 1 ? values[0] : null;
};

const requestHost = (value: string) => {
  try {
    const parsed = new URL(value);
    return parsed.host || parsed.protocol.replace(":", "");
  } catch {
    return value;
  }
};

export const McpOAuthConsentPage = () => {
  const { t } = useTranslation(["profile", "common"]);
  const navigate = useNavigate();
  const token = useAuthStore((state) => state.token);
  const hasHydrated = useAuthStore((state) => state.hasHydrated);
  const [request, setRequest] = useState<McpAuthorizationRequest | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requestAttempt, setRequestAttempt] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const requestId = useMemo(getRequestId, []);

  useEffect(() => {
    if (!hasHydrated) return;
    const destination = `${window.location.pathname}${window.location.search}`;
    const saved = saveAuthReturnDestination(destination);
    if (!token) {
      if (!saved) {
        setError(t("profile.mcpConsent.loginReturnError"));
        setIsLoading(false);
        return;
      }
      void navigate({ to: "/auth/signin", replace: true });
      return;
    }

    if (!requestId) {
      setError(t("profile.mcpConsent.requestMissing"));
      setIsLoading(false);
      return;
    }

    let isCurrent = true;
    setIsLoading(true);
    setError(null);
    getMcpAuthorizationRequest(requestId)
      .then((result) => {
        if (isCurrent) setRequest(result);
      })
      .catch(() => {
        if (isCurrent) setError(t("profile.mcpConsent.requestError"));
      })
      .finally(() => {
        if (isCurrent) setIsLoading(false);
      });
    return () => {
      isCurrent = false;
    };
  }, [hasHydrated, token, navigate, requestId, requestAttempt, t]);

  const handleDecision = async (decision: "approve" | "deny") => {
    if (!requestId || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const { redirect_url: redirectUrl } = await submitMcpAuthorizationDecision(requestId, decision);
      clearAuthReturnDestination();
      window.location.assign(redirectUrl);
    } catch {
      setError(t("profile.mcpConsent.decisionError"));
      setIsSubmitting(false);
    }
  };

  if (!hasHydrated || (!token && !error) || isLoading) return <PageSkeleton />;

  return (
    <AuthLayout>
      <section className="app-panel-raised w-full max-w-[35rem] p-5 sm:p-6" aria-labelledby="mcp-consent-title">
        <div className="flex items-start gap-4">
          <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-[var(--ds-radius-md)] bg-[var(--app-primary-soft)] text-[var(--app-primary)]">
            <FiShield aria-hidden="true" className="h-6 w-6" />
          </span>
          <div>
            <p className="app-domain-sidebar-label">{t("profile.mcp.eyebrow")}</p>
            <h1 id="mcp-consent-title" className="font-[var(--ds-font-display)] text-[1.375rem] font-medium leading-7 tracking-[-0.02em] text-[var(--app-text)] sm:text-[1.625rem] sm:leading-8">
              {t("profile.mcpConsent.title")}
            </h1>
            <p className="mt-2 text-sm leading-6 text-[var(--app-text-secondary)]">
              {t("profile.mcpConsent.description")}
            </p>
          </div>
        </div>

        {request ? (
          <>
            <div className="mt-6 rounded-[var(--ds-radius-md)] border border-[var(--app-border)] bg-[var(--app-surface-soft)] p-4">
              <p className="text-sm text-[var(--app-text-secondary)]">{t("profile.mcpConsent.clientAsks")}</p>
              <p className="mt-1 text-lg font-semibold text-[var(--app-text)]">{request.client_name}</p>
              <dl className="mt-3 grid gap-2 text-xs leading-5 text-[var(--app-text-secondary)]">
                <div className="flex flex-wrap gap-x-2">
                  <dt className="font-semibold text-[var(--app-text)]">{t("profile.mcpConsent.resourceHost")}</dt>
                  <dd className="break-all">{requestHost(request.resource)}</dd>
                </div>
                <div className="flex flex-wrap gap-x-2">
                  <dt className="font-semibold text-[var(--app-text)]">{t("profile.mcpConsent.returnHost")}</dt>
                  <dd className="break-all">{requestHost(request.redirect_uri)}</dd>
                </div>
              </dl>
              <p className="mt-4 text-sm font-semibold text-[var(--app-text)]">{t("profile.mcpConsent.permissions")}</p>
              <ul className="mt-2 space-y-2">
                {request.scopes.map((scope) => (
                  <li key={scope} className="flex items-start gap-2 text-sm leading-5 text-[var(--app-text-secondary)]">
                    <FiCheck aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-[var(--app-success)]" />
                    <span>{scope === "jerry:read" ? t("profile.mcpConsent.readPermission") : scope === "offline_access" ? t("profile.mcpConsent.renewPermission") : scope}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs leading-5 text-[var(--app-text-secondary)]">
                {t("profile.mcpConsent.renewalNote")}
              </p>
            </div>

            {error ? <MessageBanner role="alert" tone="error" className="mt-4">{error}</MessageBanner> : null}
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <AppButton type="button" tone="secondary" className="w-full sm:w-auto" disabled={isSubmitting} onClick={() => void handleDecision("deny")}>
                {t("profile.mcpConsent.deny")}
              </AppButton>
              <AppButton type="button" tone="primary" className="w-full sm:w-auto" isLoading={isSubmitting} leadingIcon={<FiCheck />} onClick={() => void handleDecision("approve")}>
                {isSubmitting ? t("common.states.saving") : t("profile.mcpConsent.approve")}
              </AppButton>
            </div>
          </>
        ) : error ? (
          <>
            <MessageBanner role="alert" tone="error" className="mt-6">{error}</MessageBanner>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              {requestId && error !== t("profile.mcpConsent.loginReturnError") ? (
                <AppButton type="button" tone="secondary" className="w-full sm:w-auto" disabled={isLoading} onClick={() => setRequestAttempt((attempt) => attempt + 1)}>
                  {t("common.actions.retry", { defaultValue: "Retry" })}
                </AppButton>
              ) : null}
              <AppButton type="button" tone="primary" className="w-full sm:w-auto" onClick={() => void navigate({ to: "/profile" })}>
                {t("profile.mcpConsent.backToProfile")}
              </AppButton>
            </div>
          </>
        ) : null}
      </section>
    </AuthLayout>
  );
};
